import * as Yup from "yup";
import ContainedButton from "components/layout/form/containedButton";
import React, { useCallback, useEffect, useState } from "react";
import TextField from "components/layout/form/textField";
import api from "services/api";

import { Formik } from "formik";
import { IoMdArrowForward } from "react-icons/io";
import { MdLockOutline } from "react-icons/md";
import { connect } from "react-redux";
import { passwordRegex } from "utils/regex";
import { toast } from "react-toastify";
import { toggleMembershipStep } from "store/actions/membership-actions";

import {
  ButtonsContainer,
  Container,
  Field,
  FormContainer,
  Header,
  InputsContainer,
  SearchDocumentIcon,
  SearchDocumentText,
  WrapSearchDocument,
} from "./styles";
import CustomToolip from "./CustomToolip";
import PasswordForceMobile from "./PasswordForceMobile";
import { useMediaQuery } from "react-responsive";
import axios from "axios";

const GENERIC_ERROR = "Ocorreu um erro interno na aplicação";

const notifyRequestError = (error) => {
  const response = error?.response;

  if (!response) {
    toast.error(GENERIC_ERROR);
    return;
  }

  if (response.status !== 401) {
    toast.error(response.data?.message || GENERIC_ERROR);
  }
};

const findValidatedContact = (contacts) =>
  (contacts || []).find((contact) => contact.isValidated === true) || null;

const ValidateRegistration = ({
  dispatch,
  document,
  location,
  phoneDdd,
  phoneNumber,
  phoneToken,
  email,
  emailToken,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState();
  const [userInfo, setUserInfo] = useState();
  const [unitId, setUnitId] = useState("");
  const [showPasswordForce, setShowPasswordForce] = useState(false);

  const showMobileMode = useMediaQuery({ maxWidth: 959 });

  const {
    accountOwnerName,
    bank,
    cardDocument,
    agencyNumber,
    accountNumber,
    unit,
    customerId,
  } = props;
  const hasDirectDebit = !!(
    accountOwnerName &&
    bank &&
    cardDocument &&
    agencyNumber &&
    accountNumber
  );

  const requestGetAccessToken = useCallback(async () => {
    const req = axios.create({
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Accept-Language": "pt-BR",
        client_id: process.env.REACT_APP_CLIENT_ID,
      },
      baseURL: process.env.REACT_APP_API_URL_OAUTH,
    });

    const header = {
      "Content-Type": "application/json",
      "Accept-Language": "pt-BR",
      Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
    };
    const parameters = {
      method: "POST",
      headers: header,
    };
    const body = {
      grant_type: "client_credentials",
    };

    let access_token = "";

    try {
      const response = await req.post("oauth/access-token", body, parameters);
      access_token = response.data.access_token;
    } catch (error) {
      notifyRequestError(error);
    }

    return access_token;
  }, []);

  const handleShowPasswordForce = useCallback(() => {
    setShowPasswordForce((prev) => !prev);
  }, [showPasswordForce]);

  const requestGetUser = useCallback(async (headers, params) => {
    try {
      const response = await api.get("industrial/customer/v1/users", {
        headers,
        params,
      });

      return response?.data?.[0] || null;
    } catch (error) {
      notifyRequestError(error);
      return null;
    }
  }, []);

  const requestGetCustomer = useCallback(async (headers, document) => {
    const params = {
      document,
      _limit: 1,
      _offset: 0,
      _expand: "CONTACT",
    };
    try {
      const response = await api.get(`industrial/customer/v1/customers`, {
        headers,
        params,
      });

      const customerAddress = response?.data?.[0]?.customerAddresses?.[0];

      if (!customerAddress) {
        return null;
      }

      setUnitId(customerAddress.unitId);
      return customerAddress.contacts;
    } catch (error) {
      notifyRequestError(error);
      return null;
    }
  }, []);

  const loadUserAndContact = useCallback(async () => {
    const access_token = await requestGetAccessToken();

    if (!access_token) {
      return null;
    }

    const headers = { access_token };
    const params = {
      login: document,
      _offset: 0,
      _limit: 100,
    };

    const user = await requestGetUser(headers, params);

    if (!user) {
      return null;
    }

    setUserInfo(user);

    const contacts = await requestGetCustomer(headers, document);
    const contact = findValidatedContact(contacts);

    if (!contact) {
      return null;
    }

    setUserData(contact);

    return { user, contact };
  }, [document, requestGetAccessToken, requestGetUser, requestGetCustomer]);

  useEffect(() => {
    loadUserAndContact();
  }, [loadUserAndContact]);

  const validationSchema = () =>
    Yup.object().shape({
      password: Yup.string()
        .required("Insira uma senha")
        .matches(
          passwordRegex,
          "Use o mínimo de 8 caracteres, com uma combinação de letras maiúsculas, minúsculas, números e símbolos."
        ),
      passwordConfirmation: Yup.string()
        .required("Insira a confirmação de sua senha")
        .oneOf([Yup.ref("password"), null], "As senhas não combinam"),
    });

  const changeToAutomaticDebit = async (userId) => {
    setLoading(true);
    const accountNumberWithoutLastDigit = accountNumber.substring(
      0,
      accountNumber.length - 1
    );
    const accountLastDigit = accountNumber.slice(accountNumber.length - 1);

    const body = {
      hasDirectDebit,
      bankId: bank,
      branch: agencyNumber,
      account: accountNumberWithoutLastDigit,
      accountCheckDigit: accountLastDigit,
      accountDocument: cardDocument,
      accountOwnerName,
      userId,
      unitId,
    };

    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
      userId,
    };

    try {
      await api.patch(
        `industrial/customer/v1/customers/${customerId}/units/${unitId}`,
        body,
        { headers }
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      notifyRequestError(err);
    }
  };

  const requestPostPassword = useCallback(
    async (user, contact, password, passwordConfirmation, token) => {
      toast.dismiss();
      const { userId } = user;
      const { contactId } = contact;
      setLoading(true);
      const access_token = await requestGetAccessToken();
      const headers = {
        access_token,
      };
      const body = {
        contactId,
        password,
        passwordConfirmation,
        token,
      };

      try {
        const responsePassword = await api.post(
          `industrial/customer/v1/users/${userId}/passwords`,
          body,
          { headers }
        );

        if (!responsePassword) {
          setLoading(false);
          toast.error(GENERIC_ERROR);
          return;
        }

        const tokenMFA = responsePassword.headers?.["x-mfa-hash"];

        if (tokenMFA) {
          localStorage.setItem("@UltragazEmpresarial:X-Mfa-Hash", tokenMFA);
        }

        if (hasDirectDebit) {
          await changeToAutomaticDebit(userId);
        }
        setLoading(false);
        await dispatch(toggleMembershipStep(7));
      } catch (err) {
        setLoading(false);
        notifyRequestError(err);
      }
    },
    [
      setLoading,
      requestGetAccessToken,
      dispatch,
      hasDirectDebit,
      unitId,
      customerId,
    ]
  );

  const handleSubmit = useCallback(
    async ({ token, password, passwordConfirmation }) => {
      if (loading) {
        return;
      }

      let user = userInfo;
      let contact = userData;

      if (!user || !contact) {
        setLoading(true);
        const reloaded = await loadUserAndContact();
        setLoading(false);

        if (!reloaded) {
          toast.error(
            "Não conseguimos carregar seus dados de cadastro. Atualize a página e tente novamente."
          );
          return;
        }

        user = reloaded.user;
        contact = reloaded.contact;
      }

      await requestPostPassword(
        user,
        contact,
        password,
        passwordConfirmation,
        token
      );
    },
    [loading, userInfo, userData, loadUserAndContact, requestPostPassword]
  );

  return (
    <Container>
      <Header>
        <h1>Estamos quase lá!</h1>
      </Header>

      <Formik
        initialValues={{
          token: phoneToken || emailToken || "",
          password: "",
          passwordConfirmation: "",
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        render={({ handleChange, handleBlur, values, errors, touched }) => (
          <FormContainer>
            <InputsContainer>
              <Field>
                <TextField
                  id="password"
                  name="password"
                  label="Senha"
                  aria-label="Senha"
                  aria-required
                  errorCondition={errors.password && touched.password}
                  errorMessage={errors.password}
                  showChangeTextPassword
                  onBlur={handleBlur}
                  placeholder="Senha"
                  value={values.password}
                  LeadingIcon={() => <MdLockOutline size={24} />}
                  onChange={handleChange}
                />
                <WrapSearchDocument>
                  {!showMobileMode && (
                    <CustomToolip title="Requisitos de Senha">
                      <SearchDocumentText>
                        Requisitos de senha
                      </SearchDocumentText>
                      <SearchDocumentIcon>!</SearchDocumentIcon>
                    </CustomToolip>
                  )}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    {showMobileMode && (
                      <div
                        style={{
                          display: "flex",
                        }}
                        onClick={handleShowPasswordForce}
                      >
                        <SearchDocumentText>
                          Requisitos de senha
                        </SearchDocumentText>
                        <SearchDocumentIcon>!</SearchDocumentIcon>
                      </div>
                    )}

                    {showMobileMode && showPasswordForce && (
                      <PasswordForceMobile />
                    )}
                  </div>
                </WrapSearchDocument>
              </Field>

              <Field>
                <TextField
                  id="passwordConfirmation"
                  name="passwordConfirmation"
                  label="Confirmação de Senha"
                  aria-label="Confirmação de Senha"
                  aria-required
                  errorCondition={
                    errors.passwordConfirmation && touched.passwordConfirmation
                  }
                  errorMessage={errors.passwordConfirmation}
                  showChangeTextPassword
                  onBlur={handleBlur}
                  placeholder="Confirme a senha"
                  value={values.passwordConfirmation}
                  LeadingIcon={() => <MdLockOutline size={24} />}
                  onChange={handleChange}
                />
              </Field>
            </InputsContainer>

            <ButtonsContainer>
              <ContainedButton
                block
                type="submit"
                continue
                loading={loading}
                disabled={!values.passwordConfirmation && !values.password}
                id="Avançar"
                width="100%"
                TrailingIcon={() => <IoMdArrowForward size={20} />}
              >
                Continuar
              </ContainedButton>
            </ButtonsContainer>
          </FormContainer>
        )}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  name: state.membershipReducer.name,
  document: state.membershipReducer.document,
  phoneDdd: state.membershipReducer.phoneDdd,
  phoneNumber: state.membershipReducer.phoneNumber,
  phoneToken: state.membershipReducer.phoneToken,
  email: state.membershipReducer.email,
  emailToken: state.membershipReducer.emailToken,
  birthdate: state.membershipReducer.birthdate,
  unit: state.membershipReducer.unit,
  address: state.membershipReducer.address,
  blockIdSf: state.membershipReducer.blockIdSf,
  condominiumIdSf: state.membershipReducer.condominiumIdSf,
  organizationId: state.membershipReducer.organizationId,
  location: state.membershipReducer.location,
  agencyNumber: state.membershipReducer.agencyNumber,
  accountNumber: state.membershipReducer.accountNumber,
  bank: state.membershipReducer.bank,
  cardDocument: state.membershipReducer.cardDocument,
  accountOwnerName: state.membershipReducer.accountOwnerName,
  customerId: state.membershipReducer.customerId,
});

export default connect(mapStateToProps)(ValidateRegistration);
